import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Form, FormLabel } from 'react-bootstrap';
import * as http from '../../http';
import useToaster from '../../actions/useToaster';
import { encrypt } from '../../actions/userActions';
import { HttpResponse } from '../../http';

interface SetupConfiguration {
  feedAet?: string;
  storageFolder?: string;
  tempFolder?: string;
  disposalFolder?: string;
  dicomPort?: number;
  worklistPort?: number;
  httpPort?: number;
  redisUrl?: string;
  redisTenant?: string;
  gloryEnabled?: boolean;
  inputStreamName?: string;
  outputStreamName?: string;
  soapListener?: string;
  hl7ForwarderEnabled?: boolean;
  hl7InputStreamName?: string;
  destination?: string;
  scriptRedisEnabled?: boolean;
  scriptInputStreamName?: string;
  scriptOutputStreamName?: string;
}

interface SetupForm extends SetupConfiguration {
  username: string;
  password?: string;
  monitoringUrl: string;
  setupCloudServer: boolean;
  monitoringEnabled: boolean;
  cloudServer: string;
  cloudHeader?: string;
  enableRedis: boolean;
}

export default function SetupPage() {
  const labelsWidth = 5;
  const inputsWidth = 9;

  const toaster = useToaster();
  const [setupLoaded, setSetupLoaded] = useState<boolean>(false);
  const [setupError, setSetupError] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  const [form, setForm] = useState<SetupForm>({
    feedAet: undefined,
    storageFolder: undefined,
    tempFolder: undefined,
    disposalFolder: undefined,
    dicomPort: undefined,
    worklistPort: undefined,
    httpPort: undefined,
    redisUrl: undefined,
    redisTenant: undefined,
    gloryEnabled: undefined,
    inputStreamName: undefined,
    outputStreamName: undefined,
    soapListener: undefined,
    hl7ForwarderEnabled: undefined,
    hl7InputStreamName: undefined,
    destination: undefined,
    scriptRedisEnabled: undefined,
    scriptInputStreamName: undefined,
    scriptOutputStreamName: undefined,

    username: 'Administrator',
    password: undefined,
    monitoringUrl: 'https://console.zeeromed.it/api/v1/inputs/feed_statuses.json?station=CONSOLE_FEED_NAME',
    setupCloudServer: false,
    monitoringEnabled: false,
    cloudServer: 'gcloud.zeeromed.it',
    cloudHeader: undefined,
    enableRedis: false,
  });

  useEffect(() => {
    http
      .get<SetupConfiguration>('/rest/react/setup')
      .then(res => {
        setForm(prev => ({
          ...prev,
          ...res,
        }));
      })
      .catch(() => {
        toaster.error('Error loading default setup');
        setSetupError(true);
      })
      .finally(() => {
        setSetupLoaded(true)
      });
  }, []);

  function onValueChange(patch: Partial<SetupForm>) {
    setForm(prev => ({ ...prev, ...patch }));
  }

  async function save() {
    setSaving(true);
    try {
      const payload: SetupForm = {
        ...form,
        password: encrypt(form.password),
      };

      await http.post<HttpResponse<string>>('/rest/react/setup', payload);
      window.location.reload();
    } catch {
      toaster.error('Configurations setup failed');
    } finally {
      setSaving(false);
    }
  }

  if (!setupLoaded) {
    return (
      <React.Fragment>
        <div>Loading setup configuration...</div>
      </React.Fragment>
    );
  }

  if (setupError) {
    return (
      <React.Fragment>
        <div>An error occurred loading the setup configuration</div>
      </React.Fragment>
    );
  }

  return (
    <Card className="page-panel-common">
      <Card.Body>
        <h3>Feed setup</h3>

        <Form style={{ maxWidth: '90rem' }}>
          <Form.Group>
            <Col as={FormLabel} sm={labelsWidth}>
              FEED AET (accepts multiple values separated by “,”)
            </Col>
            <Col sm={inputsWidth}>
              <Form.Control
                type="text"
                name="aeTitle"
                value={form.feedAet}
                onChange={e => onValueChange({ feedAet: e.target.value })}
              />
              <Form.Control.Feedback />
            </Col>
          </Form.Group>

          <Form.Group>
            <Col as={FormLabel} sm={labelsWidth}>
              Storage folder
            </Col>
            <Col sm={inputsWidth}>
              <Form.Control
                type="text"
                name="storageFolder"
                value={form.storageFolder}
                onChange={e => onValueChange({ storageFolder: e.target.value })}
              />
              <Form.Control.Feedback />
            </Col>
          </Form.Group>

          <Form.Group>
            <Col as={FormLabel} sm={labelsWidth}>
              Temp folder
            </Col>
            <Col sm={inputsWidth}>
              <Form.Control
                type="text"
                name="tempFolder"
                value={form.tempFolder}
                onChange={e => onValueChange({ tempFolder: e.target.value })}
              />
              <Form.Control.Feedback />
            </Col>
          </Form.Group>

          <Form.Group>
            <Col as={FormLabel} sm={labelsWidth}>
              Disposal folder
            </Col>
            <Col sm={inputsWidth}>
              <Form.Control
                type="text"
                name="disposalFolder"
                value={form.disposalFolder}
                onChange={e => onValueChange({ disposalFolder: e.target.value })}
              />
              <Form.Control.Feedback />
            </Col>
          </Form.Group>

          <Form.Group>
            <Col as={FormLabel} sm={labelsWidth}>
              Dicom port
            </Col>
            <Col sm={inputsWidth}>
              <Form.Control
                type="number"
                name="dicomPort"
                value={form.dicomPort}
                onChange={e => onValueChange({ dicomPort: parseInt(e.target.value) })}
              />
              <Form.Control.Feedback />
            </Col>
          </Form.Group>

          <Form.Group>
            <Col as={FormLabel} sm={labelsWidth}>
              Worklist port
            </Col>
            <Col sm={inputsWidth}>
              <Form.Control
                type="number"
                name="worklistPort"
                value={form.worklistPort}
                onChange={e => onValueChange({ worklistPort: parseInt(e.target.value) })}
              />
              <Form.Control.Feedback />
            </Col>
          </Form.Group>

          <Form.Group>
            <Col as={FormLabel} sm={labelsWidth}>
              HTTP port
            </Col>
            <Col sm={inputsWidth}>
              <Form.Control
                type="number"
                name="httpPort"
                value={form.httpPort}
                onChange={e => onValueChange({ httpPort: parseInt(e.target.value) })}
              />
              <Form.Control.Feedback />
            </Col>
          </Form.Group>

          <Form.Group>
            <Col as={FormLabel} sm={labelsWidth}>
              Privileged username
            </Col>
            <Col sm={inputsWidth}>
              <Form.Control
                type="text"
                name="adminUsername"
                value={form.username}
                onChange={e => onValueChange({ username: e.target.value })}
              />
              <Form.Control.Feedback />
            </Col>
          </Form.Group>

          <Form.Group>
            <Col as={FormLabel} sm={labelsWidth}>
              Privileged user password
            </Col>
            <Col sm={inputsWidth}>
              <Form.Control
                type="password"
                name="adminPassword"
                value={form.password}
                onChange={e => onValueChange({ password: e.target.value })}
              />
              <Form.Control.Feedback />
            </Col>
          </Form.Group>

          <Form.Group style={{ paddingTop: '1rem' }}>
            <Col sm={inputsWidth}>
              <Form.Check
                inline
                checked={form.setupCloudServer}
                onChange={e => onValueChange({ setupCloudServer: e.target.checked })}
                label="Setup a cloud server node"
              />
            </Col>
          </Form.Group>

          {form.setupCloudServer && (
            <Form.Group>
              <Col style={{ paddingLeft: '2.5rem' }}>
                <Col as={FormLabel} sm={labelsWidth}>
                  Cloud server
                </Col>
                <Col sm={inputsWidth}>
                  <Form.Control
                    type="text"
                    name="cloudServer"
                    value={form.cloudServer}
                    onChange={e => onValueChange({ cloudServer: e.target.value })}
                  />
                  <Form.Control.Feedback />
                </Col>
                <Col as={FormLabel} sm={labelsWidth} style={{ paddingTop: '0.8rem' }}>
                  Authentication header
                </Col>
                <Col sm={inputsWidth}>
                  <Form.Control
                    type="text"
                    name="cloudHeader"
                    value={form.cloudHeader}
                    onChange={e => onValueChange({ cloudHeader: e.target.value })}
                  />
                </Col>
                <Col>
                  <ul style={{ paddingTop: '0.8rem', paddingLeft: '0.5rem' }}>
                    <ListItem
                      label="STOW-RS"
                      value={`
                      https://${form.cloudServer}/o3-dpacs-wado/stowRsListener/${form.feedAet?.split(',')[0]}/studies
                      `}
                    />
                    <ListItem
                      label="QIDO"
                      value={`https://${form.cloudServer}/o3-dpacs-wado/qido/${form.feedAet?.split(',')[0]}`}
                    />
                    <ListItem label="WADO" value={`https://${form.cloudServer}/o3-dpacs-wado/wado`} />
                    <ListItem label="UPS-RS" value={`https://${form.cloudServer}/o3-dpacs-wado/workitems`} />
                  </ul>
                </Col>
              </Col>
            </Form.Group>
          )}

          <Form.Group>
            <Col sm={inputsWidth}>
              <Form.Check
                inline
                checked={form.monitoringEnabled}
                onChange={e => onValueChange({ monitoringEnabled: e.target.checked })}
                label="Setup a console endpoint"
              />
            </Col>
          </Form.Group>

          {form.monitoringEnabled && (
            <Form.Group>
              <Col style={{ paddingLeft: '2.5rem' }}>
                <Col as={FormLabel} sm={labelsWidth}>
                  Console url (also known as monitoring url)
                </Col>
                <Col sm={inputsWidth}>
                  <Form.Control
                    type="text"
                    name="monitoringUrl"
                    value={form.monitoringUrl}
                    onChange={e => onValueChange({ monitoringUrl: e.target.value })}
                  />
                  <Form.Control.Feedback />
                </Col>
              </Col>
            </Form.Group>
          )}

          <Form.Group>
            <Col sm={inputsWidth}>
              <Form.Check
                inline
                type="checkbox"
                id="enable_redis"
                checked={form.enableRedis}
                onChange={() => onValueChange({ enableRedis: !form.enableRedis })}
                label="Enable redis integration"
              />
            </Col>
          </Form.Group>

          {form.enableRedis && (
            <div style={{ paddingLeft: '2.5rem' }}>
              <React.Fragment>
                <Form.Group>
                  <Col as={FormLabel} sm={labelsWidth}>
                    Redis url
                  </Col>
                  <Col sm={inputsWidth}>
                    <Form.Control
                      type="text"
                      name="redisurl"
                      value={form.redisUrl}
                      onChange={e => onValueChange({ redisUrl: e.target.value })}
                    />
                    <Form.Control.Feedback />
                  </Col>
                </Form.Group>

                <Form.Group>
                  <Col as={FormLabel} sm={labelsWidth}>
                    Redis tenant (used as consumer group)
                  </Col>
                  <Col sm={inputsWidth}>
                    <Form.Control
                      type="text"
                      name="redistenant"
                      value={form.redisTenant}
                      onChange={e => onValueChange({ redisTenant: e.target.value })}
                    />
                    <Form.Control.Feedback />
                  </Col>
                </Form.Group>

                <Form.Group style={{ paddingTop: '0.5rem' }}>
                  <Col sm={inputsWidth}>
                    <Form.Check
                      inline
                      type="checkbox"
                      id="enable_glory"
                      label="Enable glory cash machines"
                      checked={form.gloryEnabled}
                      onChange={() => onValueChange({ gloryEnabled: !form.gloryEnabled })}
                    />
                  </Col>
                </Form.Group>

                {form.gloryEnabled && (
                  <div style={{ paddingLeft: '2.5rem' }}>
                    <React.Fragment>
                      <Form.Group>
                        <Col as={FormLabel} sm={labelsWidth}>
                          Glory redis input stream name
                        </Col>
                        <Col sm={inputsWidth}>
                          <Form.Control
                            type="text"
                            name="glory_stream_name"
                            value={form.inputStreamName}
                            onChange={e => onValueChange({ inputStreamName: e.target.value })}
                          />
                          <Form.Control.Feedback />
                        </Col>
                      </Form.Group>
                      <Form.Group>
                        <Col as={FormLabel} sm={labelsWidth}>
                          Glory redis output stream name
                        </Col>
                        <Col sm={inputsWidth}>
                          <Form.Control
                            type="text"
                            name="glory_stream_name"
                            value={form.outputStreamName}
                            onChange={e => onValueChange({ outputStreamName: e.target.value })}
                          />
                          <Form.Control.Feedback />
                        </Col>
                      </Form.Group>
                      <Form.Group>
                        <Col as={FormLabel} sm={4}>
                          Feed endpoint for glory events (remember the /glory)
                        </Col>
                        <Col sm={inputsWidth}>
                          <Form.Control
                            type="text"
                            name="glory_stream_name"
                            value={form.soapListener}
                            onChange={e => onValueChange({ soapListener: e.target.value })}
                          />
                          <Form.Control.Feedback />
                        </Col>
                      </Form.Group>
                    </React.Fragment>
                  </div>
                )}

                <Form.Group style={{ paddingTop: '0.5rem' }}>
                  <Col sm={inputsWidth}>
                    <Form.Check
                      inline
                      type="checkbox"
                      id="enable_hl7_forwarder"
                      label="Enable hl7 forwarder"
                      checked={form.hl7ForwarderEnabled}
                      onChange={() => onValueChange({ hl7ForwarderEnabled: !form.hl7ForwarderEnabled })}
                    />
                  </Col>
                </Form.Group>

                {form.hl7ForwarderEnabled && (
                  <div style={{ paddingLeft: '2.5rem' }}>
                    <React.Fragment>
                      <Form.Group>
                        <Col as={FormLabel} sm={labelsWidth}>
                          Hl7 forwarder redis input stream name
                        </Col>
                        <Col sm={inputsWidth}>
                          <Form.Control
                            type="text"
                            name="hl7_forwarder_stream_name"
                            value={form.hl7InputStreamName}
                            onChange={e => onValueChange({ hl7InputStreamName: e.target.value })}
                          />
                          <Form.Control.Feedback />
                        </Col>
                      </Form.Group>
                      <Form.Group>
                        <Col as={FormLabel} sm={4}>
                          Mirth endpoint to send the message
                        </Col>
                        <Col sm={inputsWidth}>
                          <Form.Control
                            type="text"
                            name="hl7_forwarder_stream_name"
                            value={form.destination}
                            onChange={e => onValueChange({ destination: e.target.value })}
                          />
                          <Form.Control.Feedback />
                        </Col>
                      </Form.Group>
                    </React.Fragment>
                  </div>
                )}

                <Form.Group style={{ paddingTop: '0.5rem' }}>
                  <Col sm={inputsWidth}>
                    <Form.Check
                      inline
                      type="checkbox"
                      id="enable_script_redis"
                      label="Enable script redis"
                      checked={form.scriptRedisEnabled}
                      onChange={() => onValueChange({ scriptRedisEnabled: !form.scriptRedisEnabled })}
                    />
                  </Col>
                </Form.Group>

                {form.scriptRedisEnabled && (
                  <div style={{ paddingLeft: '2.5rem' }}>
                    <React.Fragment>
                      <Form.Group>
                        <Col as={FormLabel} sm={labelsWidth}>
                          Script redis input stream name
                        </Col>
                        <Col sm={inputsWidth}>
                          <Form.Control
                            type="text"
                            name="script_redis_stream_name"
                            value={form.scriptInputStreamName}
                            onChange={e => onValueChange({ scriptInputStreamName: e.target.value })}
                          />
                          <Form.Control.Feedback />
                        </Col>
                      </Form.Group>
                      <Form.Group>
                        <Col as={FormLabel} sm={4}>
                          Script redis output stream name
                        </Col>
                        <Col sm={inputsWidth}>
                          <Form.Control
                            type="text"
                            name="script_redis_stream_name"
                            value={form.scriptOutputStreamName}
                            onChange={e => onValueChange({ scriptOutputStreamName: e.target.value })}
                          />
                          <Form.Control.Feedback />
                        </Col>
                      </Form.Group>
                    </React.Fragment>
                  </div>
                )}
              </React.Fragment>
            </div>
          )}

          <Form.Group>
            <Col sm={inputsWidth}>
              <div style={{ display: 'flex', alignItems: 'baseline', marginTop: '2rem' }}>
                <Button variant="primary" onClick={save} disabled={saving || !form.password || form.password.length === 0}>
                  {saving ? 'Saving…' : 'Save'}
                </Button>
                {(!form.password || form.password.length === 0) && (
                  <div style={{ marginTop: '1rem', marginLeft: '1rem', color: 'red' }}>
                    You need to set an admin password first!
                  </div>
                )}
              </div>
              <div style={{ marginTop: '1rem', fontSize: '0.8em' }}>*Restart the system to apply changes</div>
            </Col>
          </Form.Group>
        </Form>
      </Card.Body>
    </Card>
  );
}

function ListItem({ label, value }) {
  return (
    <li
      style={{
        whiteSpace: 'nowrap',
        listStylePosition: 'inside',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
      }}
      title={value}
    >
      {label}: {value}
    </li>
  );
}
